import React, { useEffect, useState } from "react";

import ShipeeziNewGrid, { Column, RowActions } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { PRIVATE_ROUTES } from "../../../../../constants";
import { Sort } from "../../../../../models/shipeezi.table";
import { Status } from "../../../../../util/enums";
import { useActions } from "./hooks/useActions";
import { StatusBadge } from "./styles";
import { checkResourceCan, FN_NAMES, hasPermission, isShowAction } from "Util/permissions";
import { ResourceName, ResourceCanEnum } from "Util/enums/accessRule";
import { useAppState } from "Context/StateContext";

const Account = () => {
  const { t: translate } = useTranslation();

  const history = useHistory();

  const {
    accountsData,
    getAccounts,
    getAccountsCount,
    getActiveAccountsCount,
    handleCurrentPage,
    handleFilter,
    handlePageSize,
    handleSearch,
    handleSort,
    page,
    pageSize,
    total,
    useUpdateAccount,
  } = useActions();

  const { isFetching, refetch: refetchAccounts } = getAccounts();

  getAccountsCount();

  const { refetch: refetchActiveAccountsCount } = getActiveAccountsCount();

  const { isLoading: isUpdating, mutate: updateAccount } = useUpdateAccount(() => {
    refetchAccounts();
    refetchActiveAccountsCount();
  });
  const { currentUser, userPermissions } = useAppState();
  const [accountPermission, setAccountPermission] = useState<{
    view: boolean,
    edit: boolean,
    create: boolean,
    changeStatus: boolean,
  }>({
    view: false,
    edit: false,
    create: false,
    changeStatus: false,
  });

  useEffect(() => {
    if (userPermissions && userPermissions?.length > 0) {
      setAccountPermission({
        view: hasPermission(userPermissions, FN_NAMES.FUNCTION_NETWORK_ACCOUNTS_VIEW),
        edit: checkResourceCan(userPermissions, ResourceName.ACCOUNTS, ResourceCanEnum.EDIT) && hasPermission(userPermissions, FN_NAMES.FUNCTION_NETWORK_ACCOUNTS_EDIT),
        create: checkResourceCan(userPermissions, ResourceName.ACCOUNTS, ResourceCanEnum.EDIT) && hasPermission(userPermissions, FN_NAMES.FUNCTION_NETWORK_ACCOUNTS_ADD_NEW),
        changeStatus: checkResourceCan(userPermissions, ResourceName.ACCOUNTS, ResourceCanEnum.EDIT) && hasPermission(userPermissions, FN_NAMES.FUNCTION_NETWORK_ACCOUNTS_CHANGE_STATUS),
      })
    }
  }, [userPermissions])

  const columns: Column[] = [
    { name: "name", title: translate("name") },
    {
      name: "numberOfUsers",
      title: `# ${translate("ofUsers")}`,
      formatText: (value) => value || 0,
      hideSearch: true,
      hideSort: true,
    },
    {
      name: "status",
      title: translate("status"),
      formatComponent: (value?: string) => <StatusBadge value={value} />,
      hideSearch: true,
    },
  ];

  const editAccount = (accountId: string | number) => {
    if (accountId) {
      history.push({
        pathname: `${PRIVATE_ROUTES.NETWORK_ACCOUNT_MNT}/${accountId}`,
        state: {
          accountId: Number(accountId),
          tab: "accounts",
        },
      });
    }
  };

  const changeAccountStatus = (id: number) => {
    if (accountsData && accountsData?.length) {
      const accountToChange = accountsData.find((account) => Number(account.id) === id);

      if (accountToChange) {
        const accountUpdated = {
          ...accountToChange,
          status: accountToChange.status === Status.ACTIVE ? Status.INACTIVE : Status.ACTIVE,
        };

        if (accountUpdated?.id) {
          updateAccount({ id: accountUpdated.id, account: accountUpdated });
        }
      }
    }
  };

  const rowActions: RowActions = {
    orderBy: ["handleEdit", "handleChange"],
    handleEdit: isShowAction(currentUser, accountPermission.edit)
      ? (id: string | number) => editAccount(id)
      : undefined,
    handleChange: isShowAction(currentUser, accountPermission.changeStatus)
      ? (id: string | number) => changeAccountStatus(Number(id))
      : undefined,
  };

  return (
    <div>
      <ShipeeziNewGrid
        data={accountsData}
        columns={columns}
        totalItems={total}
        loadingData={isFetching || isUpdating}
        emptyData={translate("noInformationToBeShown")}
        pageSizeList={[15, 40, 60, 100]}
        rowActions={rowActions}
        pageSize={pageSize}
        onChangePageSize={handlePageSize}
        currentPage={page}
        onNextPage={handleCurrentPage}
        onPreviousPage={handleCurrentPage}
        onGoToPage={handleCurrentPage}
        onSorting={(sorts: Sort[]) => handleSort(sorts)}
        showSelection={false}
        positionActionsColumn="right"
        generalActions={{
          orderBy: {
            left: ["handleSearch", "handleFilterBy"],
            right: ["handleNewRecord"],
          },
          handleNewRecord: isShowAction(currentUser, accountPermission.create)
            ? {
              text: translate("newAccount"),
              action: () => history.push(PRIVATE_ROUTES.NETWORK_ACCOUNT_MNT, { tab: "accounts" }),
              tooltip: translate("create"),
            }
            : undefined,
          handleSearch: {
            onSearch: (filters) => handleSearch(filters),
          },
          handleFilterBy: {
            categories: [
              {
                name: translate("status"),
                options: [Status.ACTIVE, Status.INACTIVE],
              },
            ],
            onChange: handleFilter,
          },
        }}
      />
    </div>
  );
};

export default Account;
